import {
  Box,
  ListItemButton,
  ListItemContent,
  Stack,
  Typography,
} from "@mui/joy";
import dayjs from "dayjs";
import { ThoughtRecordType } from "api/thoughtRecordApi";
import { memo } from "react";
import useOpenThoughtRecord from "hooks/useOpenThoughtRecord";
import { theme } from "styles/theme";
import { useNavigate } from "react-router-dom";
import useIsRecordSeparatedPage from "../../../hooks/useIsRecordSeparatedPage";

function ThoughtRecordListItem({
  width,
  direction = "column",
  isFirstRecordOfMonth,
  thoughtRecord,
  isSelected,
  setCurrentThoughtRecord,
  shouldBeDone,
}: {
  width: string;
  direction?: "row" | "column";
  isFirstRecordOfMonth: boolean;
  thoughtRecord: ThoughtRecordType;
  isSelected?: boolean;
  setCurrentThoughtRecord: React.Dispatch<
    React.SetStateAction<ThoughtRecordType | undefined>
  >;
  shouldBeDone?: boolean;
}) {
  const { thoughtRecordKey, date, situation, isHighlight } = thoughtRecord;
  const openThoughtRecord = useOpenThoughtRecord(thoughtRecordKey);
  const navigate = useNavigate();

  const isRecordSeparatedPage = useIsRecordSeparatedPage();

  return (
    <Stack direction={direction} key={thoughtRecordKey}>
      {isFirstRecordOfMonth && (
        <Typography
          variant={"Caption"}
          sx={{
            width: width,
            color: theme.vars.palette.Content.Strong,
            pl: "12px",
            pt: "16px",
            pb: "8px",
            opacity: 0.5,
            fontWeight: 700,
          }}
        >
          {`${dayjs(date).format("MMM")}`}
        </Typography>
      )}
      <ListItemButton
        selected={isSelected}
        sx={{
          width: width,
          p: 0,
          height: "80px",
          alignItems: "center",
          borderRadius: "16px",
        }}
        onClick={() => {
          if (isRecordSeparatedPage) {
            navigate(`/thought-record-detail?trk=${thoughtRecordKey}`); // 앱인 경우 단순 navigate 이동
          } else {
            openThoughtRecord();
          }

          setCurrentThoughtRecord(thoughtRecord);
          (document.activeElement as HTMLElement)?.blur();
        }}
      >
        <ListItemContent>
          <Stack
            key={`thoughtRecord_${thoughtRecordKey}`}
            direction={"row"}
            alignItems="center"
            sx={{
              position: "relative",
            }}
          >
            {isHighlight && (
              <Box
                sx={{
                  position: "absolute",
                  left: 0,
                  width: "4px",
                  height: "100%",
                  borderRadius: "100px",
                  backgroundColor: theme.vars.palette.Border.Highlight,
                }}
              />
            )}

            <Stack
              direction={"column"}
              justifyContent={"center"}
              alignItems="center"
              sx={{
                ...(direction === "column" && {
                  ml: "8px",
                  mr: "12px",
                }),
                ...(direction === "row" && {
                  pl: "8px",
                }),
                width: "50px",
                minWidth: "50px",
              }}
            >
              <Typography
                variant={"H3"}
                sx={{
                  py: 0,
                  color: theme.vars.palette.Content.Strong,
                  fontWeight: 590,
                }}
                {...(shouldBeDone && { color: "danger" })}
              >
                {dayjs(date).get("date")}
              </Typography>
              <Typography
                variant={"Caption"}
                sx={{
                  pb: 0,
                  color: theme.vars.palette.Content.Medium,
                  fontWeight: 400,
                }}
                {...(shouldBeDone && { color: "danger" })}
              >
                {dayjs(date).format("ddd")}
              </Typography>
            </Stack>
            <Typography
              variant={"Body"}
              sx={{
                color: theme.vars.palette.Content.Strong,
                fontWeight: 500,
                flex: 1,
                minWidth: 0,
                pl: 0,
                pr: "20px",
                opacity: direction === "row" ? 0 : 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: "2",
                WebkitBoxOrient: "vertical",
              }}
            >
              {situation}
            </Typography>
          </Stack>
        </ListItemContent>
      </ListItemButton>
    </Stack>
  );
}

export default memo(ThoughtRecordListItem);
